import { InjectionToken, WritableSignal } from '@angular/core';

/**
 * Contratto per i provider di anteprima file.
 * Ogni modulo (AI, file-system, ecc.) puo implementare il proprio provider
 * per risolvere le preview attraverso endpoint diversi.
 *
 * Il parametro `fileId` e un identificativo generico:
 * - Per il provider AI: fileId = sha256 del file
 * - Per il provider FileSystem: fileId = path relativo del file nell'archivio
 */
export interface IFilePreviewProvider {
  /**
   * Carica l'anteprima di un file dato il tipo e il fileId.
   * Il tipo di ritorno dipende dal fileType:
   * - 'pdf' → Blob | { images: string[] }
   * - 'xls'/'xlsx' → ExcelPreview
   * - 'eml'/'msg' → { htmlBody: string }
   * - 'image'/'doc'/'docx' → { images: string[] }
   */
  retrievePreview(fileType: string, fileId: string): Promise<any>;

  /**
   * Recupera il contenuto testuale (OCR) di un file.
   */
  retrieveTextContent(fileId: string): Promise<{ textContent: string }>;

  /**
   * Scarica il file originale.
   */
  downloadFile(fileId: string): Promise<Blob>;
}

/**
 * Token di iniezione per il provider di preview.
 * Ogni modulo consumatore deve fornire la propria implementazione.
 */
export const FILE_PREVIEW_PROVIDER = new InjectionToken<IFilePreviewProvider>('FILE_PREVIEW_PROVIDER');

/**
 * Contesto opzionale per il provider di preview FileSystem.
 * Permette di fornire archiveId e isRelativePath in modo reattivo
 * tramite un WritableSignal, cosi il provider legge sempre il valore aggiornato.
 */
export interface FilePreviewContext {
  archiveId: WritableSignal<string>;
  isRelativePath: WritableSignal<boolean>;
}

/**
 * Token di iniezione opzionale per il contesto del provider FileSystem.
 * Se fornito, FileSystemPreviewProvider lo usa per leggere archiveId e isRelativePath.
 * Se non fornito, il provider usa i valori di default (stringa vuota e true).
 */
export const FILE_PREVIEW_CONTEXT = new InjectionToken<FilePreviewContext>('FILE_PREVIEW_CONTEXT');
